/**
 * QweRty rateless sender.
 *
 * A transfer is an infinite, ordered stream of transport packets:
 *
 *   index 0        manifest
 *   index 1..S     RaptorQ source symbols
 *   index > S      repair symbols, generated on demand (ordinal = index-1-S)
 *
 * Because repair symbols are produced lazily with a growing repair offset,
 * every pass over the stream contributes new linear combinations instead of
 * replaying the same packets.
 */

import { RaptorQEncoder } from '@qwerty/raptorq-wasm';
import { preprocessPayload } from '../sender/preprocess_payload';
import { crc32c } from '../protocol/crc32c';
import {
  encodeManifest,
  randomTransferId,
  serializeTransportPacket,
  SYMBOL_MANIFEST,
  SYMBOL_RAPTORQ,
  type Manifest,
} from './protocol';

export interface QrSenderOptions {
  isText: boolean;
  compress?: boolean;
  filename?: string;
  mime?: string;
  /** QR payload capacity for the chosen profile (header+CRC already subtracted). */
  maxTransportPayloadSize: number;
  qrVersion: number;
  /** 0 = L, 1 = M, 2 = Q, 3 = H */
  eccLevel: number;
  parallelCount: number;
  transferId?: number;
}

export interface QrSenderInfo {
  transferId: number;
  dataLength: number;
  isText: boolean;
  isCompressed: boolean;
  symbolSize: number;
  sourceSymbols: number;
  sourceBlocks: number;
}

const MAX_REPAIR_CACHE = 8192;

export class QrSender {
  readonly transferId: number;
  readonly manifest: Manifest;
  readonly manifestPacket: Uint8Array;
  readonly sourcePackets: Uint8Array[];
  readonly symbolSize: number;
  readonly sourceSymbols: number;
  readonly sourceBlocks: number;
  readonly dataLength: number;

  private readonly encoder: RaptorQEncoder;
  private readonly isText: boolean;
  private readonly compressed: boolean;
  private readonly repairCache = new Map<number, Uint8Array>();

  private constructor(
    encoder: RaptorQEncoder,
    transferId: number,
    manifest: Manifest,
    manifestPacket: Uint8Array,
    sourcePackets: Uint8Array[],
    symbolSize: number,
    sourceSymbols: number,
    sourceBlocks: number,
    dataLength: number,
    isText: boolean,
    compressed: boolean,
  ) {
    this.encoder = encoder;
    this.transferId = transferId;
    this.manifest = manifest;
    this.manifestPacket = manifestPacket;
    this.sourcePackets = sourcePackets;
    this.symbolSize = symbolSize;
    this.sourceSymbols = sourceSymbols;
    this.sourceBlocks = sourceBlocks;
    this.dataLength = dataLength;
    this.isText = isText;
    this.compressed = compressed;
  }

  static create(data: Uint8Array, options: QrSenderOptions): QrSender {
    const preprocessed = preprocessPayload(
      data,
      options.isText,
      options.compress ?? true,
      options.filename,
      options.mime,
    );
    const encoder = new RaptorQEncoder(preprocessed.data, options.maxTransportPayloadSize);
    const transferId = options.transferId ?? randomTransferId();
    const sourceBlocks = encoder.sourceBlockCount();
    let sourceSymbols = 0;
    for (let block = 0; block < sourceBlocks; block++) {
      sourceSymbols += encoder.sourceSymbolsInBlock(block);
    }
    const symbolSize = encoder.symbolSize();
    const oti = new Uint8Array(encoder.objectTransmissionInformation());
    const payloadHash = crc32c(preprocessed.data);

    const manifest: Manifest = {
      transferId,
      isText: options.isText,
      compressed: preprocessed.isCompressed,
      totalLength: preprocessed.dataLength,
      payloadHash,
      qrVersion: options.qrVersion,
      eccLevel: options.eccLevel,
      parallelCount: options.parallelCount,
      oti,
      filename: options.filename ?? '',
      mime: options.mime ?? '',
    };

    const manifestPayload = encodeManifest(manifest);
    const manifestPacket = serializeTransportPacket(
      {
        transferId,
        symbolIndex: SYMBOL_MANIFEST,
        isText: options.isText,
        isLast: false,
        compressed: preprocessed.isCompressed,
        dataLength: manifestPayload.length,
      },
      manifestPayload,
    );

    const rawSource = encoder.sourcePackets();
    const sourcePackets = Array.from(rawSource, (packet, index) =>
      serializeTransportPacket(
        {
          transferId,
          symbolIndex: SYMBOL_RAPTORQ,
          isText: options.isText,
          isLast: index === rawSource.length - 1,
          compressed: preprocessed.isCompressed,
          dataLength: preprocessed.dataLength,
        },
        new Uint8Array(packet),
      ),
    );

    return new QrSender(
      encoder,
      transferId,
      manifest,
      manifestPacket,
      sourcePackets,
      symbolSize,
      sourceSymbols,
      sourceBlocks,
      preprocessed.dataLength,
      options.isText,
      preprocessed.isCompressed,
    );
  }

  get initialPacketCount(): number {
    return 1 + this.sourcePackets.length;
  }

  get info(): QrSenderInfo {
    return {
      transferId: this.transferId,
      dataLength: this.dataLength,
      isText: this.isText,
      isCompressed: this.compressed,
      symbolSize: this.symbolSize,
      sourceSymbols: this.sourceSymbols,
      sourceBlocks: this.sourceBlocks,
    };
  }

  /**
   * Resolve a global stream index to a transport packet. Repair packets are
   * generated lazily and cached, so the stream is effectively unbounded.
   */
  packetAt(index: number): Uint8Array {
    if (!Number.isInteger(index) || index < 0) {
      throw new RangeError('invalid packet index: ' + index);
    }
    if (index === 0) return this.manifestPacket;
    if (index <= this.sourcePackets.length) return this.sourcePackets[index - 1]!;
    return this.repairPacketAt(index - 1 - this.sourcePackets.length);
  }

  repairPacketAt(ordinal: number): Uint8Array {
    const cached = this.repairCache.get(ordinal);
    if (cached) return cached;

    const block = ordinal % this.sourceBlocks;
    const local = Math.floor(ordinal / this.sourceBlocks);
    const generated = this.encoder.repairPackets(block, local, 1);
    const payload = new Uint8Array(generated[0] as Uint8Array);
    const packet = serializeTransportPacket(
      {
        transferId: this.transferId,
        symbolIndex: SYMBOL_RAPTORQ,
        isText: this.isText,
        isLast: false,
        compressed: this.compressed,
        dataLength: this.dataLength,
      },
      payload,
    );

    if (this.repairCache.size < MAX_REPAIR_CACHE) {
      this.repairCache.set(ordinal, packet);
    }
    return packet;
  }
}
